import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete.svg";
import { ReactComponent as StreaksIcon } from "../../assets/streak.svg";
import { ReactComponent as ChartIcon } from "../../assets/chart.svg";
import { ReactComponent as DiaryIcon } from "../../assets/diary.svg";
import { ReactComponent as DollarIcon } from "../../assets/dollar.svg";
import { useDispatch, useSelector } from "react-redux";
import { setLimit, setPage } from "../../features/users/userSlice";
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Button,
  TableSortLabel,
  Tooltip,
  Typography
} from "@mui/material";
import getInitials from "../../utils/getInitials";
import dayjs from "dayjs";
import useSignedUrl from "../../utils/useSignedUrl";
import StatsListModal from "../Modals/StatsModal";
import BadgeDisplay from "../Extra/BadgeDisplay";
import NutritionTrackingModal from "../Modals/DiaryDisplay";
import {
  AddLink,
  CancelRounded,
  Chat,
  CheckCircle,
  LinkOutlined,
  PersonOff,
  CancelOutlined,
  Scale
} from "@mui/icons-material";
import {
  selectCurrentPage,
  selectLimit
} from "../../features/users/userSelectors";
import { purchaseTrainingBulk, purchaseUserTraining } from "../../api/userApi";
import { useState } from "react";
import AdminCommunityLinksModal from "../Modals/AdminCommunityLinksModal";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  enableDMApi,
  disableDMApi,
  getDMStatusApi
} from "../../api/messagingApi";
import ClientNotesModal from "../Modals/ClientNotesModal";
import { StickyNote2Outlined as NoteIcon } from "@mui/icons-material";

// Custom CSS styles
const styles = {
  tableContainer: {
    borderRadius: "10px 10px 0 0",
    "&::-webkit-scrollbar": {
      width: "4px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#374151",
      borderRadius: "4px"
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1"
    }
  },
  headerCell: {
    minWidth: 120,
    backgroundColor: "#374151",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 700,
    textAlign: "center" // Center the header text
  },
  tableRow: {
    fontSize: "12px"
  },
  activeStatus: {
    fontSize: "12px",
    color: "#2e7d32", // Darker green text for active
    backgroundColor: "#e8f5e9", // Light green background
    padding: "5px",
    borderRadius: "5px"
  },
  inactiveStatus: {
    fontSize: "12px",
    color: "#d32f2f", // Darker red text for inactive
    backgroundColor: "#ffebee", // Light red background
    padding: "5px",
    borderRadius: "5px"
  }
};

const columns = (currentTab, isAdmin, userType = "") => [
  { id: "fullName", label: "Name", minWidth: 120 },
  { id: "email", label: "Email", minWidth: 120 },
  isAdmin ? { id: "userType", label: "Role", minWidth: 60 } : null,
  isAdmin
    ? {
        id: "referenceId",
        label:
          currentTab !== "All"
            ? currentTab
            : "Coach / Gym / Company / Affiliate",
        maxWidth: 150,
        align: "center"
      }
    : null,
  isAdmin
    ? { id: "productName", label: "Product", minWidth: 60, align: "center" }
    : null,
  { id: "createdAt", label: "Joined", minWidth: 60, align: "center" },
  { id: "lastLogin", label: "Last Diary Log", minWidth: 70, align: "center" },
  {
    id: "lastCompletedDay",
    label: "Last Completed Day",
    minWidth: 70,
    align: "center"
  },
  // { id: "streak", label: "Streak", minWidth: 50, align: "center" },
  // ...(isAdmin
  //   ? []
  //   : [{ id: "badges", label: "Badges", minWidth: 110, align: "center" }]),
  userType === "Coach"
    ? {
        id: "training",
        label: "Unlock Programs",
        minWidth: 110,
        align: "center"
      }
    : null,
  { id: "userStatus", label: "Status", align: "center" },
  isAdmin && currentTab === "Coach"
    ? { id: "toc", label: "ToC Accepted", minWidth: 60 }
    : null,
  { id: "actions", minWidth: 130, align: "center" }
];
const ImageItem = ({ img }) => {
  const imgSrc = useSignedUrl(
    img,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  return (
    <Box
      component="img"
      borderRadius="50%"
      height={40}
      width={40}
      src={imgSrc}
      alt="avatar"
    />
  );
};

const DMButton = ({ userId, userName }) => {
  const [enabled, setEnabled] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  React.useEffect(() => {
    if (!userId) return;
    getDMStatusApi(userId)
      .then((data) => setEnabled(data.isEnabled))
      .catch(() => {})
      .finally(() => setLoadingStatus(false));
  }, [userId]);

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setToggling(true);
    try {
      if (enabled) {
        await disableDMApi(userId);
        setEnabled(false);
      } else {
        const res = await enableDMApi(userId);
        setEnabled(true);
        window.dispatchEvent(
          new CustomEvent("admin:openMessaging", {
            detail: { conversationId: res?.conversation?._id }
          })
        );
      }
    } catch {}
    finally {
      setToggling(false);
    }
  };

  return (
    <>
      <Tooltip title={enabled ? "Disable Direct Messaging" : "Enable Direct Messaging"}>
        <span>
          <IconButton
            onClick={() => setConfirmOpen(true)}
            disabled={toggling || loadingStatus}
            sx={{
              height: "32px",
              width: "32px",
              backgroundColor: enabled ? "#1976d2" : "#F3F4F6",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: enabled ? "#1565c0" : "#E5E7EB"
              }
            }}
            size="small"
            aria-label="direct message"
          >
            <Chat
              style={{
                height: "15px",
                width: "15px",
                color: enabled ? "#fff" : "#6B7280"
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle>
          {enabled ? "Disable Direct Messaging?" : "Enable Direct Messaging?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {enabled
              ? `Are you sure you want to disable direct messaging for ${userName}?`
              : `Are you sure you want to enable direct messaging for ${userName}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{ background: "#1976d2" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const StickyHeadTable = ({
  isAdmin = true,
  refetchUsers,
  totalUsers,
  users,
  loading,
  onEdit,
  adminData,
  setConfirmDeleteModal,
  error,
  currentTab,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  openNotesForUser = null,
  onNotesClose
}) => {
  const [openStats, setOpenStats] = React.useState({}); // Modal state
  const [page, setNewPage] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [openWeightTracker, setOpenWeightTracker] = React.useState(false);
  const [openClientNotes, setOpenClientNotes] = useState(null);

  // Open via external prop flag
  React.useEffect(() => {
    if (openNotesForUser) setOpenClientNotes(openNotesForUser);
  }, [openNotesForUser]);
  const limit = useSelector(selectLimit);

  const [rowsPerPage, setRowsPerPage] = React.useState(limit);
  const [updateLinkModal, setUpdateLinkModal] = useState(null);
  const selectCurrentPageData = useSelector(selectCurrentPage);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const dispatch = useDispatch();
  const handleChangePage = (event, newPage) => {
    setNewPage(newPage);
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setNewPage(0);
    dispatch(setPage(0));
    dispatch(setLimit(+event.target.value));
  };

  React.useEffect(() => {
    setNewPage(selectCurrentPageData);
  }, [selectCurrentPageData]);
  const getRoleChipStyle = (role) => {
    switch (role) {
      case "Gym":
        return {
          style: {
            backgroundColor: "#D7F0DE",
            fontSize: "12px",
            color: "#38B65B"
          },
          label: "Gym"
        };
      case "user":
      case "Affiliate":
        return {
          style: {
            backgroundColor: "#FCECD3",
            fontSize: "12px",
            color: "#F1B44C"
          },
          label: "User"
        };
      case "admin":
        return {
          style: {
            backgroundColor: "#D7F0DE",
            fontSize: "12px",
            color: "#38B65B"
          },
          label: "Admin"
        };
      case "Coach":
        return {
          style: {
            backgroundColor: "#FDD9CD",
            fontSize: "12px",
            color: "#F6541C"
          },
          label: "Coach"
        };
      case "Company":
        return {
          style: {
            backgroundColor: "#D4E9FC",
            fontSize: "12px",
            color: "#368AD0"
          },
          label: "Company"
        };
      default:
        return { style: {} };
    }
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
    // Update the sorting logic here as needed
  };

  const handleCheckboxChange = async (levelName, userId) => {
    try {
      const response = await purchaseUserTraining({
        userId,
        levelName
      });
      if (response?.sessionUrl) {
        window.location.href = response?.sessionUrl;
      }
    } catch (error) {
      console.error("Generating url failed::::", error?.missingLevels);
      if (error?.missingLevels?.length) {
        setSnackbarMessage(
          `${error?.error} : ${error?.missingLevels
            ?.map((item) => item?.title)
            ?.join(", ")}`
        );
        setOpenSnackbar(true);
      }
    }
  };

  const [bulkSelection, setBulkSelection] = useState({});
  const handleBulkUnlock = async () => {
    try {
      const payload = Object.entries(bulkSelection).map(([userId, levels]) => ({
        userId,
        levels
      }));

      const response = await purchaseTrainingBulk(payload);

      window.location.href = response.sessionUrl; // Stripe redirect
    } catch (error) {
      setSnackbarMessage(error?.error || "Bulk unlock failed");
      setOpenSnackbar(true);
    }
  };

  const handleLevelToggle = (userId, levelName, checked) => {
    setBulkSelection((prev) => {
      const userLevels = new Set(prev[userId] || []);
      checked ? userLevels.add(levelName) : userLevels.delete(levelName);
      return { ...prev, [userId]: [...userLevels] };
    });
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={1000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage?.includes("success") ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {adminData?.userType === "Coach" && (
        <Box display="flex" justifyContent="flex-end" p={2}>
          <Button
            disabled={Object.keys(bulkSelection).length === 0}
            onClick={handleBulkUnlock}
            sx={{
              fontSize: "14px",
              padding: "16px 24px",
              background: "#1976d2",
              border: "1px solid #1976d2",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              ":disabled": {
                backgroundColor: "#A7C8E9",
                color: "#FFF",
                pointerEvents: "none"
              },
              opacity: Object.keys(bulkSelection).length === 0 ? 0.4 : 1
            }}
          >
            Unlock Selected Levels
          </Button>
        </Box>
      )}

      <TableContainer sx={styles.tableContainer}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns(currentTab, isAdmin, adminData?.userType)
                ?.filter(Boolean)
                ?.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sortDirection={orderBy === column.id ? order : false}
                    style={{
                      ...styles.headerCell
                    }}
                  >
                    {![
                      "streak",
                      "badges",
                      "lastCompletedDay",
                      "userStatus"
                    ].includes(column.id) ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      <>{column.label}</>
                    )}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow hover role="checkbox" tabIndex={0}>
                <TableCell colSpan={12} align="center" sx={{ height: "100%" }}>
                  {/* Center the CircularProgress */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%" // Ensure the Box takes up full height of the row
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              users?.map((row) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.email}>
                  <TableCell style={styles.tableRow} align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="left"
                      gap={2}
                    >
                      {row?.profilePic ? (
                        <ImageItem img={row?.profilePic} />
                      ) : (
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {row.fullName
                            ? getInitials(row.fullName)
                            : getInitials(row.displayName)}
                        </Avatar>
                      )}
                      <Typography fontSize="small">
                        {row.fullName ? row.fullName : row.displayName}{" "}
                        {row.displayName ? `(${row.displayName})` : ""}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {row.email}
                  </TableCell>
                  {isAdmin ? (
                    <>
                      <TableCell style={styles.tableRow} align="center">
                        <Chip
                          label={
                            getRoleChipStyle(
                              row.isAdmin === 1
                                ? currentTab === "All"
                                  ? row.userType
                                  : "admin"
                                : "user"
                            ).label
                          }
                          sx={
                            getRoleChipStyle(
                              row.isAdmin === 1
                                ? currentTab === "All"
                                  ? row.userType
                                  : "admin"
                                : "user"
                            ).style
                          }
                        />
                      </TableCell>
                      <TableCell style={styles.tableRow} align="center">
                        {row.referenceId ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap={2}
                          >
                            {row?.userTypeDetails?.profilePic ? (
                              <ImageItem
                                img={row?.userTypeDetails?.profilePic}
                              />
                            ) : (
                              <Avatar sx={{ width: 40, height: 40 }}>
                                {getInitials(
                                  row.userTypeDetails?.[
                                    `${row.userType?.toLowerCase()}Name`
                                  ]
                                )}
                              </Avatar>
                            )}
                            <Typography fontSize="small">
                              {
                                row.userTypeDetails?.[
                                  `${row.userType?.toLowerCase()}Name`
                                ]
                              }
                            </Typography>
                          </Box>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    </>
                  ) : null}

                  {isAdmin && row.productName ? (
                    <TableCell style={styles.tableRow} align="center">
                      {row.productName}
                    </TableCell>
                  ) : null}
                  <TableCell style={styles.tableRow} align="center">
                    {dayjs.utc(row.createdAt).format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {row.lastLogin
                      ? dayjs.utc(row.lastLogin).format("MM/DD/YYYY")
                      : "N/A"}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {row.lastCompletedDay}
                  </TableCell>

                  {/* <TableCell style={styles.tableRow} align="center">
                    <Box
                      display={"flex"}
                      gap="7px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <StreaksIcon height={20} width={20} />
                      <Typography variant="bold">{row.streaks}</Typography>
                    </Box>
                  </TableCell> */}
                  {/* {!isAdmin && (
                  <TableCell sx={{ p: 0 }} align="center">
                    <BadgeDisplay open={row} />
                  </TableCell>
                )} */}
                  {adminData?.userType === "Coach" &&
                  row?.alreadyActiveLevel ? (
                    <TableCell align="center" style={styles.tableRow}>
                      <Box display="flex" justifyContent="center" gap={1}>
                        {[
                          { level: "level1", label: "L1", color: "primary" },
                          { level: "level2", label: "L2", color: "secondary" },
                          { level: "level3", label: "L3", color: "success" }
                        ].map((item) => (
                          <FormControlLabel
                            key={item.level}
                            sx={{
                              margin: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                                marginLeft: 0.5
                              }
                            }}
                            control={
                              <Checkbox
                                size="small"
                                color={item.color}
                                disabled={
                                  !row.isActive ||
                                  row.alreadyActiveLevel.includes(item.level)
                                }
                                checked={
                                  bulkSelection[row.userId]?.includes(
                                    item.level
                                  ) ||
                                  row.alreadyActiveLevel?.includes(
                                    item.level
                                  ) ||
                                  false
                                }
                                onChange={(e) =>
                                  handleLevelToggle(
                                    row.userId,
                                    item.level,
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label={item.label}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  ) : null}

                  <TableCell align="center" style={styles.tableRow}>
                    <Typography
                      margin={0}
                      style={
                        row.userStatus === "Active"
                          ? styles.activeStatus
                          : styles.inactiveStatus
                      }
                    >
                      {row.userStatus}
                    </Typography>
                  </TableCell>
                  {currentTab === "Coach" && (
                    <TableCell align="center" style={styles.tableRow}>
                      <Typography margin={0}>
                        {row.userTypeDetails?.isPendingCheckAgreement ===
                          undefined ||
                        row.userTypeDetails?.isPendingCheckAgreement ? (
                          <CancelRounded sx={{ color: "rgb(211, 47, 47)" }} />
                        ) : (
                          <CheckCircle sx={{ color: "rgb(46, 125, 50)" }} />
                        )}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box display="flex" gap="20px" justifyContent="flex-end">
                      {(isAdmin && !(row.userType === "Coach" && row.isAdmin === 0) || adminData?.userType === 'Coach') && (
                        <Tooltip title="Client Notes">
                          <IconButton
                            onClick={() => setOpenClientNotes(row)}
                            sx={{
                              height: "32px",
                              width: "32px",
                              backgroundColor: "#F3F4F6",
                              borderRadius: "4px",
                              "&:hover": { backgroundColor: "#E5E7EB" }
                            }}
                            size="small"
                            aria-label="client notes"
                          >
                            <NoteIcon style={{ height: "15px", width: "15px", color: "#6B7280" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(isAdmin ||
                        !["Company", "Affiliate", "Gym"].includes(
                          row.userType
                        )) && (
                        <>
                          <Tooltip title="Diary Logs">
                            <IconButton
                              onClick={() => {
                                setIsModalOpen(row);
                              }}
                              sx={{
                                height: "32px",
                                width: "32px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "4px"
                              }}
                              size="small"
                              aria-label="diary"
                            >
                              <DiaryIcon
                                style={{ height: "15px", width: "15px" }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Weight Tracker">
                            <IconButton
                              onClick={() => {
                                setOpenWeightTracker(row);
                              }}
                              sx={{
                                height: "32px",
                                width: "32px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "4px"
                              }}
                              size="small"
                              aria-label="weight tracker"
                            >
                              <Scale style={{ height: "15px", width: "15px" }} />
                            </IconButton>
                          </Tooltip>
                          <DMButton
                            userId={row.userId}
                            userName={row.fullName || row.displayName}
                          />
                          {row.isAdmin ? (
                            !row?.userTypeDetails?.isActive ? (
                              <Tooltip title="Business verification is incomplete">
                                <IconButton
                                  // disabled
                                  sx={{
                                    height: "32px",
                                    width: "32px",
                                    backgroundColor: "#F3F4F6",
                                    borderRadius: "4px",
                                    cursor: "not-allowed"
                                  }}
                                  size="small"
                                  aria-label="edit"
                                >
                                  <LinkOutlined
                                    style={{ height: "15px", width: "15px" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <IconButton
                                onClick={async () => {
                                  await navigator.clipboard.writeText(
                                    row.affiliateLink
                                  );
                                }}
                                sx={{
                                  height: "32px",
                                  width: "32px",
                                  backgroundColor: "#F3F4F6",
                                  borderRadius: "4px"
                                }}
                                size="small"
                                aria-label="edit"
                              >
                                <LinkOutlined
                                  style={{ height: "15px", width: "15px" }}
                                />
                              </IconButton>
                            )
                          ) : (
                            <></>
                          )}
                          {currentTab === "Gym" ? (
                            <IconButton
                              onClick={() => {
                                setUpdateLinkModal(row);
                              }}
                              sx={{
                                height: "32px",
                                width: "32px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "4px"
                              }}
                              size="small"
                              aria-label="edit"
                            >
                              <AddLink
                                style={{
                                  color: "#6B7280",
                                  fill: "#6B7280",
                                  height: "15px",
                                  width: "15px"
                                }}
                              />
                            </IconButton>
                          ) : (
                            <></>
                          )}
                          <IconButton
                            onClick={() => {
                              if (row.isActive) setOpenStats(row);
                            }}
                            sx={{
                              height: "32px",
                              width: "32px",
                              backgroundColor: "#F3F4F6",
                              borderRadius: "4px"
                            }}
                            size="small"
                            aria-label="edit"
                          >
                            <ChartIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {isAdmin ? (
                        <IconButton
                          sx={{
                            height: "32px",
                            width: "32px",
                            backgroundColor: "#F3F4F6",
                            borderRadius: "4px"
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const customerDashboardUrl =
                              process.env.REACT_APP_STRIPE_PUBLIC_KEY?.includes(
                                "live"
                              )
                                ? process.env
                                    .REACT_APP_STRIPE_CUSTOMER_DASHBOARD_URL
                                : process.env
                                    .REACT_APP_TEST_STRIPE_CUSTOMER_DASHBOARD_URL;
                            const customerId = row?.stripeCustomerId;
                            window.open(
                              `${customerDashboardUrl + customerId}`,
                              "_blank"
                            );
                          }}
                          size="small"
                          aria-label="view"
                        >
                          <DollarIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <></>
                      )}
                      {isAdmin ? (
                        row.userStatus === "Active" ? (
                          <Tooltip title="Set to Cancel">
                            <IconButton
                              sx={{
                                height: "32px",
                                width: "32px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "4px"
                              }}
                              onClick={() => {
                                setConfirmDeleteModal({
                                  ...row,
                                  action: "cancelSubscription"
                                });
                              }}
                              size="small"
                              aria-label="cancel subscription"
                            >
                              <CancelOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : row.userStatus?.trim() === "Set To Cancel" ? (
                          <Tooltip title="Reactivate Subscription">
                            <IconButton
                              sx={{
                                height: "32px",
                                width: "32px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "4px"
                              }}
                              onClick={() => {
                                setConfirmDeleteModal({
                                  ...row,
                                  action: "reactivateSubscription"
                                });
                              }}
                              size="small"
                              aria-label="reactivate subscription"
                              >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : <>
                        
                        {console.log("row.userStatus::::", row.userStatus)}
                        </>
                      ) : null}
                      <IconButton
                        sx={{
                          height: "32px",
                          width: "32px",
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px"
                        }}
                        onClick={() => {
                          onEdit(row);
                        }}
                        size="small"
                        aria-label="info"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {isAdmin && (
                        <Tooltip title="Remove Access">
                          <IconButton
                            sx={{
                              height: "32px",
                              width: "32px",
                              backgroundColor: "#F3F4F6",
                              borderRadius: "4px"
                            }}
                            onClick={() => {
                              setConfirmDeleteModal({
                                ...row,
                                action: "removeAccess"
                              });
                            }}
                            size="small"
                            aria-label="remove access"
                          >
                            <PersonOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            height: "32px",
                            width: "32px",
                            backgroundColor: "#F3F4F6",
                            borderRadius: "4px"
                          }}
                          onClick={() => {
                            setConfirmDeleteModal({
                              ...row,
                              action: "delete"
                            });
                          }}
                          size="small"
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <AdminCommunityLinksModal
        open={updateLinkModal}
        refetchUsers={refetchUsers}
        handleClose={() => setUpdateLinkModal(false)}
        // updateLinks={(links) => api.updateCommunityLinks(links)}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setOpenSnackbar}
      />

      <NutritionTrackingModal
        open={!!isModalOpen}
        onClose={() => setIsModalOpen(null)}
        userData={isModalOpen}
      />
      <NutritionTrackingModal
        open={!!openWeightTracker}
        onClose={() => setOpenWeightTracker(null)}
        userData={openWeightTracker}
        initialShowWeightTracker
      />
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalUsers}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <StatsListModal
        isAdmin={isAdmin}
        open={openStats}
        setOpen={setOpenStats}
      />
      <ClientNotesModal
        open={!!openClientNotes}
        onClose={() => {
          setOpenClientNotes(null);
          onNotesClose?.();
        }}
        user={openClientNotes}
      />
    </Paper>
  );
};

export default StickyHeadTable;
